// The header Items widget's rows (2026-10-29,
// docs/decisions/2026-10-29-items-widget-fast-rows-caches.md). Pure, so it
// is unit-testable.
//
// The rows come from GET /:id/order-summary's orderLines (SKU, description,
// price, quantity, kit grouping, tracking) and show at once. The slow fields
// come from GET /:id/order-summary/items in two halves: details (Stock, Tags,
// Part Details) and fitment (Fit). Each row says where its slow halves stand:
//   row.slow = { details, fit }, each 'loading' | 'ok' | 'none'
// 'none' when that half failed or brought nothing for the row: its cells
// show "—". An older server's items response ({ itemDetails }, the full
// rows) works too: it is both the rows and their slow fields.

// The slow fields a half fills in, by half.
export const DETAIL_FIELDS = Object.freeze(['quantityAvailable', 'warranty', 'electricalPart', 'kitIncludes', 'qualifiers', 'itemSpecifications', 'companySpecifications', 'ebayTitleAM', 'amSku'])
export const FIT_FIELDS = Object.freeze(['fitment'])

export function lineKey(row) {
  return `${String(row?.sku ?? '').trim().toUpperCase()}::${row?.lineSequence ?? ''}`
}

// key -> the entries with that key, in order (two lines can share a sku and
// a lineSequence; each takes the next one).
function byKey(list) {
  const map = new Map()
  for (const e of Array.isArray(list) ? list : []) {
    const k = lineKey(e)
    if (!map.has(k)) map.set(k, [])
    map.get(k).push(e)
  }
  return map
}

function pick(entry, fields) {
  const out = {}
  for (const f of fields) if (entry && f in entry) out[f] = entry[f]
  return out
}

// half: { status: 'loading' | 'loaded' | 'error', rows: [...] } or null
// (not asked: treated as loading until it is).
function halfState(half) {
  if (!half) return 'loading'
  return half.status === 'loaded' ? 'loaded' : half.status === 'error' ? 'error' : 'loading'
}

// lines: the rows (orderLines, or an older server's itemDetails). details /
// fitment: the two halves. Returns the merged rows, in the lines' order.
export function mergeOrderRows(lines, { details = null, fitment = null } = {}) {
  if (!Array.isArray(lines)) return null
  const detailsState = halfState(details)
  const fitState = halfState(fitment)
  const detailsBy = byKey(details?.rows)
  const fitBy = byKey(fitment?.rows)
  const take = (map, k) => {
    const list = map.get(k)
    return list?.length ? list.shift() : null
  }
  return lines.map((line) => {
    const k = lineKey(line)
    const d = detailsState === 'loaded' ? take(detailsBy, k) : null
    const f = fitState === 'loaded' ? take(fitBy, k) : null
    const vehicle = d?.vehicle ?? f?.vehicle ?? line.vehicle ?? null
    return {
      ...line,
      ...pick(d, DETAIL_FIELDS),
      ...pick(f, FIT_FIELDS),
      vehicle,
      slow: {
        details: detailsState === 'loading' ? 'loading' : d ? 'ok' : 'none',
        fit: fitState === 'loading' ? 'loading' : f ? 'ok' : 'none',
      },
    }
  })
}

// The rows for the order now shown, never another order's (the stale-data
// guard): orderLines from the summary fetched for this order, else the rows
// an items response for this order carried. null while neither is in.
//   summary: orderSummary ({ _for, orderLines })
//   items: { for, details, fitment, lines }
export function orderRowsFor(erpOrderId, summary, items) {
  if (!erpOrderId) return null
  const fresh = items?.for === erpOrderId ? items : null
  const lines = (summary?._for === erpOrderId && Array.isArray(summary?.orderLines)) ? summary.orderLines
    : (Array.isArray(fresh?.lines) ? fresh.lines : null)
  if (!lines) return null
  return mergeOrderRows(lines, { details: fresh?.details ?? null, fitment: fresh?.fitment ?? null })
}

// One items response half as stored: the slow fields when the server sent
// them, else an older server's full rows; and the rows it carried.
export function itemsHalfFrom(data) {
  const rows = Array.isArray(data?.slowFields) ? data.slowFields : (Array.isArray(data?.itemDetails) ? data.itemDetails : [])
  const lines = Array.isArray(data?.orderLines) ? data.orderLines : (Array.isArray(data?.itemDetails) ? data.itemDetails : null)
  return { status: 'loaded', rows, lines }
}
